import { useEffect, useState } from "react"
import { isTechLive, techLiveWithinMs, useTechClock } from "../../core/TechLive"

/**
 * How old a technician's position is, said the way somebody reading a map needs to hear it.
 *
 * A bare clock time answers the wrong question. What the dispatcher is actually asking is "can
 * I trust this dot" — and the answer is a live one, a few minutes old, or old enough that the
 * date matters. So the label changes shape with the age instead of always being a time.
 */

export interface TechLocationFreshness {
    label: string
    /** Reported within the last couple of minutes. Not "live" on its own — the app reports in
     * the background, so live also needs them on the clock; see isTechLive. */
    fresh: boolean
    /** Old enough that the dot is a guess. */
    stale: boolean
}

/** Under this the point is "just now" rather than a number of minutes. */
const justNowWithinMs = 60 * 1000

/** Past this, a time of day is no longer enough: the day it happened is the point. */
const datedAfterMs = 12 * 60 * 60 * 1000

export function techLocationFreshness(
    lastUpdate?: Date | string,
    options?: { isWakeupAddress?: boolean }
): TechLocationFreshness {
    // Not where they are — where they start from. Saying "2 hours ago" about a home address
    // would be inventing a report that never happened.
    if (options?.isWakeupAddress) {
        return { label: "Wake-up address", fresh: false, stale: false }
    }

    if (!lastUpdate) return { label: "Location never reported", fresh: false, stale: true }

    const at = new Date(lastUpdate)
    if (isNaN(at.getTime())) return { label: "Location never reported", fresh: false, stale: true }

    const age = Date.now() - at.getTime()

    // "Just now", not "Live": a recent point says when the phone last spoke, not that the
    // technician is working — that is the clock's to say.
    if (age < justNowWithinMs) return { label: "Just now", fresh: true, stale: false }

    if (age >= datedAfterMs) {
        return {
            label: at.toLocaleString(undefined, {
                month: "short",
                day: "numeric",
                year: at.getFullYear() === new Date().getFullYear() ? undefined : "numeric",
                hour: "numeric",
                minute: "2-digit"
            }),
            fresh: false,
            stale: true
        }
    }

    const minutes = Math.floor(age / 60000)

    if (minutes < 60) return { label: ago(minutes, "minute"), fresh: age < techLiveWithinMs, stale: minutes >= 30 }

    return { label: ago(Math.floor(minutes / 60), "hour"), fresh: false, stale: true }
}

function ago(count: number, unit: "minute" | "hour"): string {
    return `${count} ${unit}${count === 1 ? "" : "s"} ago`
}

interface IProps {
    /** Whose it is — so the dot can say whether they are on the clock. */
    techId?: string
    lastUpdate?: Date | string
    isWakeupAddress?: boolean
    className?: string
    /** Where the row already has its own icon (the technician drawer), the dot is one too many. */
    noDot?: boolean
}

/**
 * The same thing with its dot: green while they are live (on the clock and reporting), grey
 * otherwise — and "Offline" in front once they have clocked out, because a point from five
 * minutes ago means something else entirely when they have gone home.
 */
/**
 * The colour the label is in — green while live, grey once off the clock or stale — for a row
 * that colours its own icon to match (the drawer).
 */
export function useTechLocationTone(techId?: string, lastUpdate?: Date | string, isWakeupAddress?: boolean): string {
    const clock = useTechClock(techId)
    const freshness = techLocationFreshness(lastUpdate, { isWakeupAddress })
    const live = isTechLive(techId, lastUpdate)
    const offline = clock !== undefined && !clock.on

    return live ? "success" : offline || freshness.stale ? "gray-500" : "gray-700"
}

export default function TechLocationFreshnessLabel(props: IProps) {

    const clock = useTechClock(props.techId)

    // The age moves on by itself: "Just now" becomes "1 minute ago" without a new point, and
    // reports now come only every five minutes standing still. A screen that stays open (the
    // drawer) redraws it every half minute; one rendered once (the map's panel) is rebuilt by
    // the map instead.
    const [, tickSet] = useState(0)
    useEffect(() => {
        const timer = window.setInterval(() => tickSet(t => t + 1), 30000)
        return () => window.clearInterval(timer)
    }, [])

    const freshness = techLocationFreshness(props.lastUpdate, {
        isWakeupAddress: props.isWakeupAddress
    })

    const live = isTechLive(props.techId, props.lastUpdate)
    const offline = clock !== undefined && !clock.on

    const tone = live ? "success" : offline || freshness.stale ? "gray-500" : "gray-700"

    return (
        <span className={`d-inline-flex align-items-center text-${tone} ${props.className ?? ""}`}>
            {!props.noDot &&
                <span
                    className={`d-inline-block rounded-circle me-2 bg-${live ? "success" : "gray-400"}`}
                    style={{ width: 7, height: 7 }}
                />
            }
            {offline ? `Offline · ${freshness.label}` : freshness.label}
        </span>
    )
}
